(() => {
  const updateHeaderCartCount = (itemCount) => {
    const count = Math.max(0, Number(itemCount) || 0);
    document.querySelectorAll('[data-babaa-cart-count]').forEach((node) => {
      node.textContent = String(count);
      node.hidden = count === 0;
    });
    document.querySelectorAll('[data-babaa-cart-link]').forEach((link) => {
      const label = link.getAttribute('aria-label') || '';
      const base = label.replace(/\s*\(\d+\)\s*$/, '').trim();
      link.setAttribute('aria-label', `${base} (${count})`);
    });
    document.dispatchEvent(new CustomEvent('babaa:cart:updated', { detail: { itemCount: count } }));
  };

  const bindGiftCard = (root) => {
    if (root.dataset.bound) return;
    root.dataset.bound = 'true';

    const form = root.querySelector('.babaa-gift-card__form');
    const idField = form?.querySelector('[data-variant-id-input]');
    const submit = form?.querySelector('[data-add-to-basket]');
    const amountButtons = form ? [...form.querySelectorAll('[data-gift-card-variant]')] : [];

    const soldOutLabel = submit?.dataset.soldOutLabel || 'Sold out';
    const addLabel = submit?.dataset.addLabel || submit?.textContent || '';

    const formError = form?.querySelector('[data-form-error]');
    const errorText = formError ? formError.dataset : {};
    const recipient = form?.querySelector('[data-gift-card-recipient]');
    const recipientToggle = recipient?.querySelector('[data-recipient-toggle]');
    const recipientFields = recipient?.querySelector('[data-recipient-fields]');
    const offsetField = recipient?.querySelector('[data-recipient-offset]');
    const messageCount = recipient?.querySelector('[data-message-count]');
    const fields = {};
    const fieldErrors = {};
    recipient?.querySelectorAll('[data-recipient-field]').forEach((field) => {
      fields[field.dataset.recipientField] = field;
    });
    recipient?.querySelectorAll('[data-recipient-error]').forEach((node) => {
      fieldErrors[node.dataset.recipientError] = node;
    });

    const toIsoDate = (date) => {
      const pad = (value) => String(value).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    };

    const clearErrors = () => {
      Object.entries(fieldErrors).forEach(([key, node]) => {
        node.textContent = '';
        node.hidden = true;
        fields[key]?.removeAttribute('aria-invalid');
      });
      if (formError) {
        formError.textContent = '';
        formError.hidden = true;
      }
    };

    const showErrors = (errors, generalMessage) => {
      clearErrors();
      let firstInvalid = null;
      Object.entries(errors).forEach(([key, message]) => {
        const node = fieldErrors[key];
        const field = fields[key];
        if (!node || !field) return;
        node.textContent = message;
        node.hidden = false;
        field.setAttribute('aria-invalid', 'true');
        if (!firstInvalid) firstInvalid = field;
      });
      if (formError && generalMessage) {
        formError.textContent = generalMessage;
        formError.hidden = false;
      }
      firstInvalid?.focus();
    };

    const updateMessageCount = () => {
      if (!messageCount || !fields.message) return;
      const template = errorText.charactersTemplate || '[current]/[max]';
      messageCount.textContent = template
        .replace('[current]', String(fields.message.value.length))
        .replace('[max]', fields.message.maxLength > 0 ? String(fields.message.maxLength) : '200');
    };

    const setRecipientEnabled = (enabled) => {
      if (!recipientFields || !recipientToggle) return;
      recipientFields.disabled = !enabled;
      recipientFields.hidden = !enabled;
      recipientToggle.setAttribute('aria-expanded', enabled ? 'true' : 'false');
      if (!enabled) clearErrors();
    };

    const isSendingToRecipient = () => Boolean(recipientToggle?.checked);

    const validateRecipient = () => {
      const errors = {};
      const { email, name, message, send_on: sendOn } = fields;

      if (email) {
        email.value = email.value.trim();
        if (email.validity.valueMissing) errors.email = errorText.errorEmailRequired;
        else if (!email.validity.valid) errors.email = errorText.errorEmailInvalid;
      }
      if (name && name.value.length > 255) errors.name = errorText.errorNameTooLong;
      if (message && message.value.length > 200) errors.message = errorText.errorMessageTooLong;
      if (sendOn && (sendOn.validity.badInput || sendOn.value)) {
        const value = sendOn.value;
        const outOfRange = !/^\d{4}-\d{2}-\d{2}$/.test(value) || value < sendOn.min || value > sendOn.max;
        if (sendOn.validity.badInput || outOfRange) errors.send_on = errorText.errorSendOnInvalid;
      }

      return errors;
    };

    if (recipient && recipientToggle) {
      const today = new Date();
      const lastDay = new Date(today);
      lastDay.setDate(lastDay.getDate() + 90);
      if (fields.send_on) {
        fields.send_on.min = toIsoDate(today);
        fields.send_on.max = toIsoDate(lastDay);
      }
      if (offsetField) offsetField.value = String(today.getTimezoneOffset());

      recipientToggle.addEventListener('change', () => setRecipientEnabled(recipientToggle.checked));
      Object.entries(fields).forEach(([key, field]) => {
        field.addEventListener('input', () => {
          if (key === 'message') updateMessageCount();
          if (!field.hasAttribute('aria-invalid')) return;
          field.removeAttribute('aria-invalid');
          if (fieldErrors[key]) {
            fieldErrors[key].textContent = '';
            fieldErrors[key].hidden = true;
          }
        });
      });

      setRecipientEnabled(recipientToggle.checked);
      updateMessageCount();
    }

    const resetRecipient = () => {
      if (!recipientToggle) return;
      Object.values(fields).forEach((field) => {
        field.value = '';
      });
      recipientToggle.checked = false;
      setRecipientEnabled(false);
      updateMessageCount();
    };

    const readServerErrors = (payload) => {
      const fieldMessages = {};
      let general = '';
      const errors = payload?.errors;

      if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
        Object.entries(errors).forEach(([key, value]) => {
          const message = Array.isArray(value) ? value.join(', ') : String(value);
          if (fields[key] && fieldErrors[key]) fieldMessages[key] = message;
          else general = general ? `${general} ${message}` : message;
        });
      } else if (typeof errors === 'string') {
        general = errors;
      }

      if (!general && !Object.keys(fieldMessages).length) {
        general = payload?.description || payload?.message || '';
      }
      if (Object.keys(fieldMessages).length && !general) general = errorText.genericError || '';

      return { fieldMessages, general: general || errorText.genericError || '' };
    };

    const selectVariant = (button) => {
      if (!button || !idField || !submit) return;

      const variantId = button.dataset.variantId;
      const available = button.dataset.available === 'true';

      amountButtons.forEach((btn) => {
        const selected = btn === button;
        btn.classList.toggle('is-selected', selected);
        btn.setAttribute('aria-checked', selected ? 'true' : 'false');
      });

      idField.value = variantId;
      submit.disabled = !available;
      submit.textContent = available ? addLabel : soldOutLabel;
    };

    amountButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        selectVariant(button);
      });
    });

    if (!form || !submit) return;

    const cartAddUrl = root.dataset.cartAddUrl;
    const cartUrl = root.dataset.cartUrl;
    let adding = false;

    const addToCart = async () => {
      if (!cartAddUrl || !idField || adding || submit.disabled) return;

      const variantId = Number(idField.value);
      if (!variantId) return;

      clearErrors();
      if (isSendingToRecipient()) {
        const errors = validateRecipient();
        if (Object.keys(errors).length) {
          showErrors(errors, errorText.genericError);
          return;
        }
      }

      const addedLabel = submit.dataset.addedLabel || addLabel;
      const addingLabel = submit.dataset.addingLabel || addLabel;

      adding = true;
      submit.disabled = true;
      submit.textContent = addingLabel;

      try {
        const addResponse = await fetch(cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        const addPayload = await addResponse.json().catch(() => ({}));
        if (!addResponse.ok) {
          const { fieldMessages, general } = readServerErrors(addPayload);
          showErrors(fieldMessages, general);
          submit.disabled = false;
          submit.textContent = addLabel;
          return;
        }

        resetRecipient();

        if (cartUrl) {
          const cartResponse = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            updateHeaderCartCount(cart.item_count);
          }
        }

        submit.textContent = addedLabel;
        submit.disabled = false;
        window.setTimeout(() => {
          submit.textContent = addLabel;
        }, 1800);
      } catch (_error) {
        showErrors({}, errorText.genericError);
        submit.disabled = false;
        submit.textContent = addLabel;
      } finally {
        adding = false;
      }
    };

    form.noValidate = true;
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      addToCart();
    });
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-babaa-gift-card]').forEach(bindGiftCard);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
